import Phaser from "phaser";
import { BORG } from "../../config";
import { loadSprite, validateTextures } from "../../utils/textureLoader";

export type BorgSize = "large" | "medium" | "small";

export interface BorgCubeSprite extends Phaser.Physics.Arcade.Sprite {
  borgSize: BorgSize;
  health: number;
  maxHealth: number;
}

// Borg texture keys (1024x1024 sprites for proper mipmapping)
const BORG_TEXTURES = {
  ACTIVE: "borg-active",
  DAMAGED: "borg-damaged",
  CRITICAL: "borg-critical",
  DESTROYED: "borg-destroyed",
} as const;

// Scale factors to fit 1024px sprite to game sizes
const BORG_SCALES = {
  large: 0.06, // ~61px
  medium: 0.04, // ~41px
  small: 0.025, // ~26px
} as const;

/**
 * Preload all Borg-related sprites.
 * Call this in the scene's preload() method.
 */
export function preloadBorgSprites(loader: Phaser.Loader.LoaderPlugin): void {
  for (const key of Object.values(BORG_TEXTURES)) {
    loadSprite(loader, key);
  }
}

export class BorgManager {
  private scene: Phaser.Scene;
  cubes!: Phaser.Physics.Arcade.Group;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  create(): void {
    // Validate textures were loaded and have correct dimensions
    validateTextures(this.scene.textures, Object.values(BORG_TEXTURES));

    this.cubes = this.scene.physics.add.group();
  }

  private getTextureForHealth(healthPercent: number): string {
    if (healthPercent > 66) return BORG_TEXTURES.ACTIVE;
    if (healthPercent > 33) return BORG_TEXTURES.DAMAGED;
    return BORG_TEXTURES.CRITICAL;
  }

  spawn(size: BorgSize, shipX?: number, shipY?: number): BorgCubeSprite {
    const config = BORG[size.toUpperCase() as keyof typeof BORG] as {
      speed: number;
      health: number;
    };

    // Spawn position - away from player
    let x: number;
    let y: number;
    do {
      x = Phaser.Math.Between(50, 750);
      y = Phaser.Math.Between(50, 550);
    } while (
      shipX !== undefined &&
      shipY !== undefined &&
      Phaser.Math.Distance.Between(x, y, shipX, shipY) < BORG.MIN_SPAWN_DISTANCE
    );

    // Create cube with loaded texture
    const cube = this.cubes.create(x, y, BORG_TEXTURES.ACTIVE) as BorgCubeSprite;
    cube.borgSize = size;
    cube.setScale(BORG_SCALES[size]);

    // Set velocity
    const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
    cube.setVelocity(
      Math.cos(angle) * config.speed,
      Math.sin(angle) * config.speed,
    );

    // Rotation
    cube.setAngularVelocity(Phaser.Math.Between(-50, 50));

    // Health based on size
    cube.health = config.health;
    cube.maxHealth = config.health;

    return cube;
  }

  spawnAtPosition(size: BorgSize, x: number, y: number): BorgCubeSprite {
    const cube = this.spawn(size);
    cube.setPosition(x, y);
    return cube;
  }

  updateDamageVisual(cube: BorgCubeSprite): void {
    const healthPercent = (cube.health / cube.maxHealth) * 100;
    const newTexture = this.getTextureForHealth(healthPercent);
    if (cube.texture.key !== newTexture) {
      cube.setTexture(newTexture);
    }
  }

  updateScreenWrap(
    screenWrap: (sprite: Phaser.Physics.Arcade.Sprite) => void,
  ): void {
    this.cubes.children.each((cube) => {
      screenWrap(cube as Phaser.Physics.Arcade.Sprite);
      return true;
    });
  }

  getActiveCount(): number {
    return this.cubes.countActive();
  }

  getDamage(size: BorgSize): number {
    return BORG.DAMAGE[size];
  }

  getPoints(size: BorgSize): number {
    const config = BORG[size.toUpperCase() as keyof typeof BORG] as {
      points: number;
    };
    return config.points;
  }
}
